import { api } from './api';

export interface AdminUserSummary {
  id: string;
  email: string | null;
  name: string | null;
  countryCode: string | null;
  isSiteAdmin: boolean;
  suspendedAt: string | null;
  suspendedReason: string | null;
  emailVerified: boolean;
  hasPassword: boolean;
  oauthLinked: string[];
  createdAt: string;
}

export interface AdminCompanySummary {
  id: string;
  name: string;
  countryCode: string;
  plan: string;
  seatLimit: number;
  deploymentKind: string;
  createdAt: string;
  memberCount: number;
}

export interface AdminMetrics {
  users: { total: number; verified: number; suspended: number; siteAdmins: number };
  companies: { total: number; byPlan: Record<string, number> };
  documents: { total: number; indexed: number; failed: number };
  aiRuns7d: { runs: number; cents: number };
}

export interface AdminAuditEntry {
  id: string;
  actor_id: string;
  action: string;
  target_kind: string;
  target_id: string;
  payload: unknown;
  created_at: string;
}

export interface AdminEmailRow {
  id: string;
  user_id: string | null;
  to_email: string;
  kind: 'verify' | 'reset' | 'welcome' | 'admin_broadcast' | 'other';
  subject: string;
  status: 'sent' | 'failed' | 'skipped';
  error_text: string | null;
  created_at: string;
}

export interface QueueCounts {
  waiting: number; active: number; delayed: number; completed: number; failed: number;
}

export interface QueueMetrics {
  enabled: boolean;
  queues: Record<string, QueueCounts>;
}

export const adminApi = {
  async metrics(): Promise<AdminMetrics> {
    const r = await api.get<AdminMetrics>('/api/admin/metrics');
    return r.data;
  },
  async listUsers(opts: { search?: string; limit?: number; offset?: number } = {}) {
    const r = await api.get<{ rows: AdminUserSummary[]; total: number }>('/api/admin/users', { params: opts });
    return r.data;
  },
  async setAdmin(userId: string, isAdmin: boolean): Promise<void> {
    await api.patch(`/api/admin/users/${userId}/admin`, { isAdmin });
  },
  async setSuspended(userId: string, suspended: boolean, reason: string | null): Promise<void> {
    await api.patch(`/api/admin/users/${userId}/suspend`, { suspended, reason });
  },
  async listCompanies(opts: { search?: string; limit?: number; offset?: number } = {}) {
    const r = await api.get<{ rows: AdminCompanySummary[]; total: number }>('/api/admin/companies', { params: opts });
    return r.data;
  },
  async recentAudit(limit = 100): Promise<AdminAuditEntry[]> {
    const r = await api.get<AdminAuditEntry[]>('/api/admin/audit', { params: { limit } });
    return r.data;
  },
  async queueMetrics(): Promise<QueueMetrics> {
    const r = await api.get<QueueMetrics>('/api/admin/queues');
    return r.data;
  },
  async recentEmails(limit = 100): Promise<AdminEmailRow[]> {
    const r = await api.get<AdminEmailRow[]>('/api/admin/emails', { params: { limit } });
    return r.data;
  },
};
